/**
 * 语音对练页(V29,启动书 docs/2026-08-04-coaching-voice-START.md 批次D)。
 *
 * 全程无录音按钮 —— 半双工状态机(任何时刻只有一方在说,绕开 webview 回声消除不可控的死穴):
 *
 *   【聆听】开麦 + WebAudio 能量 VAD ──说完静音 2s──▶【思考】关麦,ASR → chat → TTS
 *       ▲                                                │
 *       └──────播放结束自动回聆听◀──【说话】播 replyAudioUrl──┘
 *
 * - 起始屏一次点击干三件事:要麦克风权限 + 解锁音频自动播放 + 建会话(拒权停在起始屏,不降级文本)
 * - 皮克斯小人 = 状态可视化(聆听呼吸圈 / 思考点点点 / 说话声波),治 4~8s 管线时延的等待感
 * - 字幕照旧上屏:文字是评分/逐字稿/续聊的唯一真相源,语音只是皮
 * - 兜底:ASR 空文本丢弃不上屏;TTS 缺失退纯文字轮(短暂停留后回聆听);静默 20s 视觉提示
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Dialog, NavBar, SpinLoading, Toast } from "antd-mobile";
import { wecomApi } from "../wecomApi";
import { useWakeLock } from "./useWakeLock";
import { useRingback } from "./useRingback";
import { briefingOf, hasSeenBriefing, markBriefingSeen } from "./briefing";
import { ScoreResultCard } from "./ScoreResultCard";
import type {
  CoachingSessionApi,
  SessionActiveResp,
  SessionResultResp,
} from "./coachingSessionApi";
import partnerFemaleImg from "./assets/voice-partner.png";
import partnerMaleImg from "./assets/voice-partner-male.png";
import { customerLookOf } from "./customerLook";
import type { CoachingScenarioItem, ChatMessage, CoachingChatResponse } from "./types";
import { errorText } from "@/shared/lib/apiErrors";

/**
 * 语音对练数据面适配器(2026-08-04 批次E):同一半双工状态机跑在两个身份栈上 ——
 * wecom-h5(MEMBER cookie,渠道成员)与 advisor-h5(STAFF JWT,TENANT_CHAIN 任务卡落地页)。
 *
 * 会话那几条(chatStart/chat/result/sessionActive/history/finish)与文本页**逐字相同**,
 * 已收进 {@link CoachingSessionApi};这里只补语音独有的 asr / abandon。
 */
export interface VoiceCoachingApi extends CoachingSessionApi {
  asr(sessionId: string, blob: Blob, filename: string): Promise<{ text: string }>;
  /** 放弃在途会话(「重新开始一次」):只清续练索引。缺省实现 = 不放弃,老行为。 */
  abandon?(sessionId: string): Promise<void>;
}

/** 默认实现 = 企微 MEMBER 端(wecom-h5.html 陪练页),行为与抽适配器前逐字一致。 */
const wecomVoiceApi: VoiceCoachingApi = {
  chatStart: (scenarioId, taskId) =>
    wecomApi.post<CoachingChatResponse>("/wecom-h5/coaching/chat/start", { scenarioId, taskId }),
  chat: (sessionId, message, scenarioId) =>
    wecomApi.post<CoachingChatResponse>("/wecom-h5/coaching/chat", {
      sessionId,
      message,
      scenarioId,
    }),
  asr: (sessionId, blob, filename) => {
    const fd = new FormData();
    fd.append("sessionId", sessionId);
    fd.append("file", blob, filename);
    return wecomApi.postForm<{ text: string }>("/wecom-h5/coaching/asr", fd);
  },
  result: (sessionId) =>
    wecomApi.get<SessionResultResp>(`/wecom-h5/coaching/sessions/${sessionId}/result`),
  sessionActive: (scenarioId, taskId) =>
    wecomApi.get<SessionActiveResp>(
      taskId
        ? `/wecom-h5/coaching/session/active?taskId=${taskId}`
        : `/wecom-h5/coaching/session/active?scenarioId=${scenarioId}`
    ),
  history: (sessionId) =>
    wecomApi.get<{ turns: { role: string; content: string }[] }>(
      `/wecom-h5/coaching/sessions/${sessionId}/history`
    ),
  abandon: (sessionId) =>
    wecomApi.post<void>("/wecom-h5/coaching/session/abandon", { sessionId }).then(() => undefined),
  finish: (sessionId, scenarioId) =>
    wecomApi.post<CoachingChatResponse>("/wecom-h5/coaching/chat/finish", {
      sessionId,
      scenarioId,
    }),
};

interface Props {
  scene: CoachingScenarioItem;
  /** 任务 ID(H5 入口 URL 携带);非空启用续聊检测,同文本页。 */
  taskId?: number;
  onBack: () => void;
  /** 数据面注入(缺省 = 企微 MEMBER 端;advisor-h5 传 STAFF 实现)。 */
  voiceApi?: VoiceCoachingApi;
}

type Phase =
  | "start"
  | "starting"
  | "resume"
  | "listening"
  | "thinking"
  | "speaking"
  | "paused"
  | "finished";

/** 暂停成因 —— 决定文案和「继续」按钮要不要重新拿麦克风。 */
type PauseReason = "hidden" | "mic-lost";

/** 对练进行中(该亮屏、该拦切后台)的三个态。 */
const LIVE_PHASES: Phase[] = ["listening", "thinking", "speaking"];

// ── 半双工调参(启动书判据) ────────────────────────────────
const SILENCE_END_MS = 2000; // 说完判定:静音 2s(老板拍板,不是 1s)
const SPEECH_RMS = 0.025; // 能量阈值(byte 域归一化 RMS)
const SPEECH_CONFIRM_FRAMES = 2; // 连续 2 帧(~200ms)超阈值才算开口,滤瞬时噪声
const VAD_TICK_MS = 100;
const MAX_UTTERANCE_MS = 60_000; // 单段说话上限
const PRE_SPEECH_RESET_MS = 10_000; // 未开口时定期重启录音,防止长静音撑大上传
const IDLE_HINT_MS = 20_000; // 聆听 20s 没开口 → 视觉提示
const MIN_TURNS_TO_FINISH = 2; // 说满 2 轮才给交卷出口(同 advisor 文本页,防误触空卷)
// 一小段静音 wav:起始屏手势里先播它解锁 autoplay(webview 政策:后续程序化播放必须有手势前科)
const SILENT_WAV =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQAAAAA=";

// 拨号等太久时的两级降级文案(纯观感:后端还在跑,不打断)
const DIAL_SLOW_MS = 10_000;
const DIAL_VERY_SLOW_MS = 25_000;
const RING_MUTED_KEY = "coaching.ringback.muted";

function readRingMuted(): boolean {
  try {
    return localStorage.getItem(RING_MUTED_KEY) === "1";
  } catch {
    return false; // 隐私模式/禁 storage:当没静音,不因为读不到设置就哑了
  }
}

function pickRecorderMime(): string {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return "";
  if (MediaRecorder.isTypeSupported("audio/mp4")) return "audio/mp4"; // iOS WKWebView
  if (MediaRecorder.isTypeSupported("audio/webm")) return "audio/webm"; // 安卓 XWeb/Chrome
  return "";
}

/**
 * 环境自检(2026-08-04 真机排障:点「开始对练」没弹权限框 → 先分清是哪一层不支持)。
 * ok=false 时 reason 直接显示在起始屏,把「无弹框」变成可读的具体病因。
 */
function detectVoiceSupport(): { ok: boolean; reason: string } {
  // 显式 false 才判死:undefined = 环境没实现该属性(老 webview/jsdom),交给下面的能力探测
  if (window.isSecureContext === false) {
    return { ok: false, reason: "页面不在安全上下文(需 HTTPS),浏览器禁用了录音能力" };
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    return {
      ok: false,
      reason:
        "当前企微内置浏览器没有开放 H5 录音接口(getUserMedia 不存在),请升级企业微信到最新版,或用手机系统浏览器打开本页试试",
    };
  }
  if (typeof MediaRecorder === "undefined") {
    return {
      ok: false,
      reason: "当前浏览器不支持录音组件(MediaRecorder 不存在),请升级企业微信/系统 WebView 后重试",
    };
  }
  return { ok: true, reason: "" };
}

/** getUserMedia 失败 → 人话病因(iOS 上企微 App 自身没系统麦克风权限时会「不弹框直接拒」)。 */
function explainMicError(e: unknown): string {
  const name = (e as { name?: string })?.name ?? "";
  const msg = (e as { message?: string })?.message ?? String(e);
  if (name === "NotAllowedError" || name === "PermissionDeniedError") {
    return (
      "麦克风权限被拒绝(如果根本没看到弹框,通常是企业微信 App 自己没有系统麦克风权限):\n" +
      "iOS:手机 设置 → 企业微信 → 麦克风 打开后,杀掉企微重新进本页;\n" +
      "安卓:系统设置 → 应用 → 企业微信 → 权限 → 麦克风 允许。"
    );
  }
  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return "没有检测到麦克风设备,请确认手机麦克风可用。";
  }
  if (name === "NotReadableError") {
    // 1017:安卓企微 WebView 里企微 App 自己没系统麦克风权限时 Chromium 报的也是 NotReadableError,
    // 只说「被占用」会把用户引去关不存在的应用 —— 先给权限路径,再提占用
    return (
      "麦克风暂时打不开。请按顺序检查:\n" +
      "① 企业微信有没有系统麦克风权限(安卓:系统设置 → 应用 → 企业微信 → 权限 → 麦克风 允许;" +
      "iOS:设置 → 企业微信 → 麦克风);\n" +
      "② 是否正在通话、录音或开着企微会议悬浮窗。\n" +
      "处理后彻底退出企业微信再进本页。"
    );
  }
  return `拿麦克风失败:${name || "未知错误"} ${msg}`.slice(0, 160);
}

/** 小人三态:聆听(呼吸光圈)/ 思考(点点点)/ 说话(声波)。纯 CSS,不做对嘴型(V1)。 */
function PartnerAvatar({ phase, src }: { phase: Phase; src: string }) {
  const speaking = phase === "speaking";
  const listening = phase === "listening";
  const thinking = phase === "thinking" || phase === "starting";
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative h-36 w-36">
        {/* 聆听:呼吸光圈(用户知道「该我说了」) */}
        {listening && (
          <>
            <span className="absolute inset-0 rounded-full bg-emerald-400/30 animate-ping" />
            <span className="absolute -inset-2 rounded-full border-2 border-emerald-400/40 animate-pulse" />
          </>
        )}
        {/* 说话:外圈声波 */}
        {speaking && (
          <span className="absolute -inset-2 rounded-full border-2 border-indigo-400/50 animate-pulse" />
        )}
        <img
          src={src}
          alt="AI 客户"
          className={`relative h-36 w-36 rounded-full object-cover object-top border-4 shadow-lg transition-all ${
            speaking
              ? "border-indigo-400"
              : listening
                ? "border-emerald-400"
                : "border-white opacity-95"
          }`}
        />
        {/* 思考:托腮泡泡(点点点) */}
        {thinking && (
          <div className="absolute -right-1 -top-1 flex items-center gap-0.5 rounded-full bg-white px-2 py-1.5 shadow">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce"
                style={{ animationDelay: `${i * 150}ms` }}
              />
            ))}
          </div>
        )}
        {/* 说话:嘴边声波条 */}
        {speaking && (
          <div className="absolute -right-1 bottom-2 flex items-end gap-0.5 rounded-full bg-white px-2 py-1.5 shadow">
            {[10, 16, 8, 14].map((h, i) => (
              <span
                key={i}
                className="w-1 rounded-full bg-indigo-500 animate-pulse"
                style={{ height: `${h}px`, animationDelay: `${i * 120}ms` }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function VoiceCoachingChat({ scene, taskId, onBack, voiceApi = wecomVoiceApi }: Props) {
  const { t } = useTranslation("coaching");
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<Phase>("start");
  const [micDenied, setMicDenied] = useState(false);
  // 真机排障:具体病因(环境不支持 / 拒权分型),起始屏直接显示
  const [micReason, setMicReason] = useState<string | null>(null);
  const [support] = useState(() => detectVoiceSupport());
  // 鸿蒙企微实锤(2026-08-04):ArkWeb 的 getUserMedia 既不弹框也不返回,Promise 永挂 →
  // 「接通中」卡死。不能加硬超时(正常机型弹框期间 Promise 同样挂起,会误杀慢点授权的用户),
  // 改成:挂 6s 亮提示 + 取消逃生门,继续等。startGen 让被取消的旧尝试的迟到 resolve 作废。
  const [startStuck, setStartStuck] = useState(false);
  const startGenRef = useRef(0);
  // V2 L1 接通仪式:拿到麦克风之后、第一句到达之前 = 「拨号中」(LLM 2~4s + TTS 1~3s)。
  // 与「正在接通」分开:之前这两段共用一个托腮动画,用户分不清是在等接通还是在等回答。
  const [dialing, setDialing] = useState(false);
  const [dialWaitMs, setDialWaitMs] = useState(0);
  const [ringMuted, setRingMuted] = useState(() => readRingMuted());
  // V2 L2 任务简报:第一次练这个场景时,起始屏本身就是简报(不多加一次点击);
  // 练过的人恢复原样,想看再点开。
  const briefing = useMemo(() => briefingOf({ ...scene, name: scene.name }), [scene]);
  // 1025:客户头像跟场景走(男声客户不再顶着女性插画)
  const partnerImg = customerLookOf(scene) === "male" ? partnerMaleImg : partnerFemaleImg;
  const [showBriefing, setShowBriefing] = useState(() => !hasSeenBriefing(scene.id));
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [hint, setHint] = useState<string | null>(null);
  // V2 批次A:切后台/锁屏/麦克风被抢都归到「已暂停」这一个态,不让状态机在后台空转
  const [pauseReason, setPauseReason] = useState<PauseReason | null>(null);
  const [evalStatus, setEvalStatus] = useState<"evaluating" | "completed" | "failed">("evaluating");
  const [score, setScore] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [isLowConfidence, setIsLowConfidence] = useState(false);
  const [highlights, setHighlights] = useState<string[]>([]);
  const [improvements, setImprovements] = useState<string[]>([]);

  const bottomRef = useRef<HTMLDivElement>(null);
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  // 状态机涉及大量 interval/回调,全部走 ref 镜像避免闭包吃旧值
  const phaseRef = useRef<Phase>("start");
  const sessionIdRef = useRef<string | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const playerRef = useRef<HTMLAudioElement | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const discardRef = useRef(false); // onstop 时丢弃(纯静音重启/退出),不进 ASR
  const mimeRef = useRef<string>("");
  const finishedRef = useRef(false);
  // 暂停时把「本该干的下一件事」存起来,点继续时原样执行(聆听 / 续播 / 播在途请求的回包)
  const pausedResumeRef = useRef<(() => void) | null>(null);

  const setPhaseBoth = (p: Phase) => {
    phaseRef.current = p;
    setPhase(p);
  };

  // ── 录音 ────────────────────────────────────────────────
  const startRecorder = () => {
    const stream = streamRef.current;
    if (!stream) return;
    chunksRef.current = [];
    discardRef.current = false;
    const rec = mimeRef.current
      ? new MediaRecorder(stream, { mimeType: mimeRef.current })
      : new MediaRecorder(stream);
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    rec.onstop = () => {
      if (discardRef.current) return;
      const blob = new Blob(chunksRef.current, { type: mimeRef.current || "audio/webm" });
      chunksRef.current = [];
      void handleUtterance(blob);
    };
    rec.start();
    recorderRef.current = rec;
  };

  const stopRecorder = (discard: boolean) => {
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") {
      discardRef.current = discard;
      try {
        rec.stop();
      } catch {
        // 已停/流失效,忽略
      }
    }
    recorderRef.current = null;
  };

  const startListening = () => {
    if (finishedRef.current) return;
    // 暂停期间绝不开麦(用户可能已经切去看别的了)—— 把动作挂起,点「继续对练」时再执行
    if (phaseRef.current === "paused") {
      pausedResumeRef.current = () => startListening();
      return;
    }
    setHint(null);
    setPhaseBoth("listening");
    startRecorder();
  };

  // ── VAD:聆听态每 100ms 量一次能量,静音 2s 判说完 ─────────
  useEffect(() => {
    if (phase !== "listening") return;
    const analyser = analyserRef.current;
    if (!analyser) return;
    const buf = new Uint8Array(analyser.fftSize);
    let voicedFrames = 0;
    let speechStarted = false;
    let lastVoiceTs = Date.now();
    let recStart = Date.now(); // 录音重启时钟(防长静音撑大上传)
    const idleStart = Date.now(); // 静默提示时钟(不随录音重启清零)
    let speechStartTs = 0;

    const tick = setInterval(() => {
      if (phaseRef.current !== "listening") return;
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / buf.length);
      const now = Date.now();

      if (rms >= SPEECH_RMS) {
        voicedFrames += 1;
        if (!speechStarted && voicedFrames >= SPEECH_CONFIRM_FRAMES) {
          speechStarted = true;
          speechStartTs = now;
          setHint(null);
        }
        if (speechStarted) lastVoiceTs = now;
      } else {
        voicedFrames = 0;
      }

      if (speechStarted) {
        // 说完判定:静音持续 2s;或单段顶到 60s 墙
        if (now - lastVoiceTs >= SILENCE_END_MS || now - speechStartTs >= MAX_UTTERANCE_MS) {
          clearInterval(tick);
          stopRecorder(false); // onstop → handleUtterance
        }
      } else {
        // 未开口:定期重启录音丢掉纯静音;20s 给视觉提示(像真客户催一句)
        if (now - recStart >= PRE_SPEECH_RESET_MS) {
          stopRecorder(true);
          startRecorder();
          recStart = now;
        }
        if (now - idleStart >= IDLE_HINT_MS) {
          setHint("还在吗?对方在等你开口…");
        }
      }
    }, VAD_TICK_MS);
    return () => clearInterval(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // ── 一段话:ASR → 上屏 → chat → 播回复 ────────────────────
  const handleUtterance = async (blob: Blob) => {
    const sessionId = sessionIdRef.current;
    if (!sessionId || finishedRef.current) return;
    setPhaseBoth("thinking");
    try {
      const ext = (mimeRef.current || "audio/webm").includes("mp4") ? "mp4" : "webm";
      const asr = await voiceApi.asr(sessionId, blob, `turn.${ext}`);
      const text = (asr?.text ?? "").trim();
      if (!text) {
        // 静音/噪声:丢弃不上屏不进 LLM(启动书兜底③)
        setHint("没听清,请再说一遍");
        startListening();
        return;
      }
      const userMsgId = `${Date.now()}-u`;
      setMessages((prev) => [...prev, { id: userMsgId, role: "user", content: text }]);

      let res: CoachingChatResponse;
      try {
        res = await voiceApi.chat(sessionId, text, scene.id);
      } catch (chatFailed) {
        // 服务端没收到这句 → 撤回刚上屏的气泡。留着的话续练恢复后它会凭空消失,
        // 而且评分依据从来只看服务端 turns(文本页一直是这么做的,语音页漏了)。
        setMessages((prev) => prev.filter((m) => m.id !== userMsgId));
        throw chatFailed;
      }
      setMessages((prev) => [
        ...prev,
        { id: `${Date.now()}-a`, role: "assistant", content: res.reply },
      ]);
      if (res.finished) {
        finishedRef.current = true;
        setScore(res.score ?? null);
        setFeedback(res.feedback ?? null);
      }
      playReply(res.replyAudioUrl ?? null, !!res.finished);
    } catch (e) {
      Toast.show({ icon: "fail", content: errorText(e) });
      startListening(); // 网络抖动不终结对练,回聆听重说
    }
  };

  // ── 播放客户台词;播完回聆听(半双工:播放期间不开麦) ────────
  const playReply = (url: string | null, finished: boolean) => {
    // 请求在途时用户切了后台:回包别抢着播(手机在兜里放声音),挂起等「继续」
    if (phaseRef.current === "paused") {
      pausedResumeRef.current = () => playReply(url, finished);
      return;
    }
    const after = () => {
      if (finished) {
        teardownMic();
        setPhaseBoth("finished");
      } else {
        startListening();
      }
    };
    const player = playerRef.current;
    if (!url || !player) {
      // TTS 失败降级:文字已上屏,停 1.5s 让用户看完字幕(一格坏不否决整份产物)
      setPhaseBoth("speaking");
      setTimeout(after, 1500);
      return;
    }
    setPhaseBoth("speaking");
    player.src = url;
    player.onended = () => {
      after();
    };
    player.onerror = () => {
      after();
    };
    player.play().catch(() => {
      after();
    });
  };

  // ── 交卷(V2 批次D) ──────────────────────────────────────
  /**
   * 提前结束并评分。之前语音页没有这个出口 —— 想收工只能一直说到 AI 判 finished,
   * 或者直接退出(=白练)。说满 2 轮才亮,判据同 advisor 文本页,防误触空卷。
   */
  const finishNow = async () => {
    const sessionId = sessionIdRef.current;
    if (!sessionId || finishedRef.current || !voiceApi.finish) return;
    finishedRef.current = true; // 先合闸:交卷期间别让 VAD 再起一轮
    stopRecorder(true);
    teardownMic();
    setPhaseBoth("finished");
    try {
      await voiceApi.finish(sessionId, scene.id);
    } catch (e) {
      Toast.show({ icon: "fail", content: errorText(e) });
      // 交卷失败:评估轮询照跑(后台可能已经在评),不把用户卡在中间态
    }
  };

  // ── 续练(V2 批次B) ──────────────────────────────────────
  /** 接着上一局练:字幕已在 messages 里,直接轮到用户说。 */
  const continuePractice = () => {
    startListening();
  };

  /**
   * 重新开始一次:**必须先放弃旧会话**。不放弃的话旧索引还挂 6h,用户下次进来又被
   * 「续上」到这一局早就不想要的对话里。放弃失败不挡着新建(顶多是下次再问一次)。
   */
  const restartFromResume = async () => {
    const stale = sessionIdRef.current;
    setPhaseBoth("starting");
    setDialing(true); // 重开也是一次接通,同样给拨号感
    if (stale) {
      try {
        await voiceApi.abandon?.(stale);
      } catch {
        // 清索引失败:新建照走,旧索引会被新会话覆盖或自然过期
      }
    }
    sessionIdRef.current = null;
    setMessages([]);
    try {
      const res = await voiceApi.chatStart(scene.id, taskId);
      sessionIdRef.current = res.sessionId ?? null;
      setDialing(false);
      setMessages([{ id: "0", role: "assistant", content: res.reply }]);
      playReply(res.replyAudioUrl ?? null, false);
    } catch (e) {
      setDialing(false);
      Toast.show({ icon: "fail", content: errorText(e) });
      setPhaseBoth("start");
    }
  };

  // ── 暂停 / 恢复(V2 批次A:切后台、锁屏、麦克风被抢) ────────
  /**
   * 进「已暂停」。**必须立刻停录音并丢弃**:页面进后台后 VAD 的 setInterval 被节流甚至冻结,
   * 判不出「静音 2s = 说完」,录音却还在涨 —— 不丢就是几分钟静音喂进 ASR(既烧钱又是垃圾轮次)。
   */
  const enterPaused = (reason: PauseReason) => {
    if (!LIVE_PHASES.includes(phaseRef.current)) return;
    const from = phaseRef.current;
    stopRecorder(true); // discard=true:这段不进 ASR
    const player = playerRef.current;
    if (from === "speaking" && player) {
      try {
        player.pause();
      } catch {
        // 播放器已失效,忽略
      }
      pausedResumeRef.current = () => {
        setPhaseBoth("speaking");
        player.play().catch(() => startListening()); // 续播失败就回聆听,不卡住
      };
    } else if (from === "listening") {
      pausedResumeRef.current = () => startListening();
    }
    // thinking:回包到达时 playReply 自己会挂起(见上),这里不预设动作
    try {
      void (audioCtxRef.current as { suspend?: () => Promise<void> } | null)?.suspend?.();
    } catch {
      // 不支持 suspend 的环境:resume 时也会跳过,不影响
    }
    setPauseReason(reason);
    setPhaseBoth("paused");
  };

  /**
   * 点「继续对练」。**必须在这次点击(用户手势)里 resume AudioContext** —— iOS 上被打断的
   * AudioContext 只有手势内 resume 才稳,程序化恢复会静默失败,表现为「回来以后 VAD 再也不动」。
   */
  const resumePractice = async () => {
    const reason = pauseReason;
    setPauseReason(null);
    setPhaseBoth("thinking"); // 过渡态:让挂起的动作脱离 paused 守卫
    if (reason === "mic-lost") {
      // 麦克风已经被系统收走,流是死的 —— 必须重拿,不能只 resume
      const ok = await reacquireMic();
      if (!ok) return;
      pausedResumeRef.current = null;
      startListening();
      return;
    }
    try {
      await audioCtxRef.current?.resume?.();
    } catch {
      // resume 失败不阻断:下面的动作里若 VAD 真死,60s 墙和用户重进仍是出口
    }
    const action = pausedResumeRef.current;
    pausedResumeRef.current = null;
    if (action) action();
    else startListening();
  };

  /** 麦克风被抢后重拿:流 + 分析器都要重建(旧的已经死了)。 */
  const reacquireMic = async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      attachTrackWatch(stream);
      const Ctx =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (Ctx) {
        void audioCtxRef.current?.close?.().catch(() => undefined);
        const ctx = new Ctx();
        void ctx.resume?.();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        source.connect(analyser);
        audioCtxRef.current = ctx;
        analyserRef.current = analyser;
      }
      return true;
    } catch (e) {
      setPauseReason("mic-lost");
      setPhaseBoth("paused");
      Toast.show({ icon: "fail", content: explainMicError(e) });
      return false;
    }
  };

  /** 轨道被系统收走(来电 / 微信语音通话抢麦)→ 进暂停,别让界面继续转圈。 */
  const attachTrackWatch = (stream: MediaStream) => {
    stream.getAudioTracks?.().forEach((track) => {
      track.onended = () => enterPaused("mic-lost");
    });
  };

  // 切后台/锁屏 → 暂停。**只在对练进行中拦**:起始屏和结果页切走不该有任何反应。
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") enterPaused("hidden");
    };
    document.addEventListener("visibilitychange", onVisibility);
    // pagehide:iOS 上息屏/切 App 有时只发这个,不发 visibilitychange
    window.addEventListener("pagehide", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 屏幕常亮:只在对练进行中持锁(暂停/结束/退出都还锁,免得用户回列表页手机还亮着)
  const wakeLock = useWakeLock(LIVE_PHASES.includes(phase));

  // 拨号回铃:接起(第一句到达)即止,不和 AI 的话叠在一起
  useRingback(dialing, ringMuted);

  // 拨号计时:只驱动降级文案,不打断请求(后端还在跑)
  useEffect(() => {
    if (!dialing) {
      setDialWaitMs(0);
      return;
    }
    const startedAt = Date.now();
    const timer = setInterval(() => setDialWaitMs(Date.now() - startedAt), 1000);
    return () => clearInterval(timer);
  }, [dialing]);

  const toggleRingMuted = () => {
    setRingMuted((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(RING_MUTED_KEY, next ? "1" : "0");
      } catch {
        // 存不下就只在本次会话生效
      }
      return next;
    });
  };

  // ── 起始屏一次点击:要权限 + 解锁播放 + 建/续会话 ──────────
  /** 「接通中」逃生门:作废本次尝试(迟到的 getUserMedia resolve 由 gen 判废),回起始屏。 */
  const cancelStart = () => {
    setDialing(false);
    startGenRef.current += 1;
    setStartStuck(false);
    teardownMic();
    setPhaseBoth("start");
  };

  const handleStart = async () => {
    setMicDenied(false);
    setMicReason(null);
    setStartStuck(false);
    markBriefingSeen(scene.id); // 简报读过了:下次进来直接给开始按钮
    if (!support.ok) {
      // 环境层就不支持:不进 starting,起始屏直接亮病因
      setMicDenied(true);
      setMicReason(support.reason);
      return;
    }
    const gen = ++startGenRef.current;
    setPhaseBoth("starting");
    // 6s 还挂着(既没弹框也没拒)→ 亮「可能不支持」提示,但继续等(用户可能正在看弹框)
    const stuckTimer = setTimeout(() => {
      if (startGenRef.current === gen && phaseRef.current === "starting") setStartStuck(true);
    }, 6000);
    // 1) 麦克风权限(拒了停在起始屏给指引,不降级文本)。约束用最朴素的 {audio:true}:
    // 显式 echoCancellation/noiseSuppression 走 native 约束处理,是 webview 崩溃常见触发点,
    // 且半双工(播放时关麦)本就不依赖 AEC。
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (startGenRef.current !== gen) {
        // 用户已点「取消」:迟到的流立刻关掉,不许留后台开麦
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      attachTrackWatch(stream); // 来电/微信语音抢麦 → 进暂停,不静默死
    } catch (e) {
      if (startGenRef.current !== gen) return;
      setMicDenied(true);
      setMicReason(explainMicError(e));
      setPhaseBoth("start");
      return;
    } finally {
      clearTimeout(stuckTimer);
      if (startGenRef.current === gen) setStartStuck(false);
    }
    // 2) 手势内解锁自动播放(播一段静音 wav)
    const player = new Audio(SILENT_WAV);
    player.play().catch(() => {
      // 解锁失败也继续:后续 play() 会再试,失败走 after 降级
    });
    playerRef.current = player;
    // 3) WebAudio 分析器(VAD 用)
    try {
      const Ctx =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (Ctx) {
        const ctx = new Ctx();
        void ctx.resume?.();
        const source = ctx.createMediaStreamSource(streamRef.current);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        source.connect(analyser);
        audioCtxRef.current = ctx;
        analyserRef.current = analyser;
      }
    } catch {
      // 无 WebAudio 就无 VAD,当场提示后退回起始屏(语音场景 VAD 是硬依赖)
      teardownMic();
      Toast.show({ icon: "fail", content: "当前浏览器不支持语音对练,请升级企微版本" });
      setPhaseBoth("start");
      return;
    }
    mimeRef.current = pickRecorderMime();
    // 权限拿到了 → 进「拨号中」。回铃从这里开始响,接起(第一句到达)即止。
    setDialing(true);

    // 4) 续练检测:有在途 session → **先给续练屏**,不悄悄开麦。
    // 之前是恢复气泡后直接进聆听 —— 用户既不知道自己在续练,也没有「重新开始」的出口,
    // 还得往回滚才看得到客户上一句说了什么。
    try {
      const active = await voiceApi.sessionActive?.(scene.id, taskId);
      if (active?.sessionId) {
        sessionIdRef.current = active.sessionId;
        try {
          const history = await voiceApi.history?.(active.sessionId);
          if (history?.turns?.length) {
            setMessages(
              history.turns.map((turn, i) => ({
                id: String(i),
                role: turn.role === "user" ? "user" : "assistant",
                content: turn.content,
              }))
            );
          }
        } catch {
          setMessages([{ id: "0", role: "assistant", content: t("wecomChat.resumeHint") }]);
        }
        setDialing(false); // 续练不用等 LLM,直接接起
        setPhaseBoth("resume");
        return;
      }
    } catch {
      // 续练检测失败走新建,不打扰用户
    }
    try {
      const res = await voiceApi.chatStart(scene.id, taskId);
      sessionIdRef.current = res.sessionId ?? null;
      setDialing(false); // 接起:回铃止,紧接着播第一句
      setMessages([{ id: "0", role: "assistant", content: res.reply }]);
      // 开场白也要播(拍板①):第一句就有声,不出戏
      playReply(res.replyAudioUrl ?? null, false);
    } catch (e) {
      setDialing(false);
      Toast.show({ icon: "fail", content: errorText(e) });
      teardownMic();
      if (taskId) {
        // 任务路径被拒(已完成/过期)→ 自动返回主页,同文本页
        queryClient.invalidateQueries({ queryKey: ["wecom-my-home"] });
        setTimeout(() => onBackRef.current(), 1200);
      }
      setPhaseBoth("start");
    }
  };

  const teardownMic = () => {
    stopRecorder(true);
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    void audioCtxRef.current?.close().catch(() => undefined);
    audioCtxRef.current = null;
    analyserRef.current = null;
  };

  // 卸载兜底:麦克风/播放器全停,绝不留后台开麦
  useEffect(() => {
    return () => {
      finishedRef.current = true;
      teardownMic();
      if (playerRef.current) {
        playerRef.current.pause();
        playerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 字幕滚到底
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // 评估轮询(与文本页同款:finished 后最长 ~2min)
  useEffect(() => {
    if (phase !== "finished") return;
    if (evalStatus !== "evaluating") return;
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 60;

    const poll = async () => {
      if (cancelled) return;
      if (attempts >= maxAttempts) {
        setEvalStatus("failed");
        setFeedback("评分暂时没出来(网络或服务繁忙),成绩会在后台继续生成,稍后可回来查看。");
        return;
      }
      attempts += 1;
      try {
        const res = await voiceApi.result(sessionId);
        if (cancelled) return;
        if (res.status === "completed") {
          setEvalStatus("completed");
          setScore(res.score);
          setFeedback(res.summary);
          setIsLowConfidence(res.isLowConfidence === 1);
          setHighlights(res.highlights ?? []);
          setImprovements(res.improvements ?? []);
          queryClient.invalidateQueries({ queryKey: ["wecom-my-home"] });
          return;
        }
        if (res.status === "failed") {
          setEvalStatus("failed");
          setFeedback(res.error ?? t("wecomChat.evalFailed"));
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 3000);
      }
    };
    const timer = setTimeout(poll, 2000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, evalStatus, queryClient]);

  /** 学员已经说了几轮 —— 交卷出口和离开挽留都看它。 */
  const turnsSaid = messages.filter((m) => m.role === "user").length;

  const leave = () => {
    queryClient.invalidateQueries({ queryKey: ["wecom-my-home"] });
    onBack();
  };

  /**
   * 离开挽留:练到一半点返回时,先告诉用户「进度留着」,别让人以为白练了。
   * 只在真的说过话且还没交卷时拦 —— 起始屏/结果页点返回不打扰。
   */
  const handleBack = () => {
    if (turnsSaid === 0 || finishedRef.current || phaseRef.current === "finished") {
      leave();
      return;
    }
    Dialog.show({
      title: "先停一下?",
      content: `已经练了 ${turnsSaid} 轮。进度会留着,下次进来能接着练。`,
      closeOnAction: true,
      actions: [
        [
          { key: "stay", text: "继续练" },
          { key: "quit", text: "保存并退出" },
        ],
        ...(voiceApi.finish && turnsSaid >= MIN_TURNS_TO_FINISH
          ? [[{ key: "finish", text: "结束并评分", bold: true }]]
          : []),
      ],
      onAction: (action) => {
        if (action.key === "quit") leave();
        if (action.key === "finish") void finishNow();
      },
    });
  };

  /**
   * 自主练习「再练一次」:回起始屏从头开始(新 session 由 handleStart 建)。
   * 必须把 finishedRef 一并放开 —— 它是状态机的总闸,不放开新一轮开不了口。
   */
  const restartPractice = () => {
    finishedRef.current = false;
    sessionIdRef.current = null;
    pausedResumeRef.current = null;
    setMessages([]);
    setScore(null);
    setFeedback(null);
    setHighlights([]);
    setImprovements([]);
    setIsLowConfidence(false);
    setEvalStatus("evaluating");
    setHint(null);
    setPauseReason(null);
    setPhaseBoth("start");
  };

  const stateLabel: Record<Phase, string> = {
    start: "",
    starting: "正在接通…",
    listening: "请说话,说完停 2 秒即可",
    thinking: "对方在想…",
    speaking: "对方正在说…",
    resume: "",
    paused: "",
    finished: "",
  };

  // ── 续练屏:说清练到哪了 + 客户上一句,再让用户决定接着练还是重开 ──────
  if (phase === "resume") {
    const said = messages.filter((m) => m.role === "user").length;
    const lastFromCustomer = [...messages].reverse().find((m) => m.role === "assistant");
    return (
      <div className="flex flex-col h-full">
        <NavBar onBack={handleBack} className="border-b border-border bg-card shrink-0">
          {scene.title ?? scene.name}
        </NavBar>
        <div className="flex-1 flex flex-col items-center justify-center gap-5 px-8 bg-muted">
          <PartnerAvatar phase="resume" src={partnerImg} />
          <div className="text-center space-y-1">
            <p className="text-base font-semibold text-slate-900">继续上次的对练</p>
            <p className="text-xs text-slate-500">
              {said > 0 ? `已经练了 ${said} 轮` : "刚开了个头"}
            </p>
          </div>
          {lastFromCustomer && (
            <div className="w-full rounded-xl bg-white border border-slate-200 px-4 py-3 shadow-sm">
              <p className="text-2xs text-slate-400 mb-1">客户上一句</p>
              <p className="text-sm text-slate-900 leading-relaxed">{lastFromCustomer.content}</p>
            </div>
          )}
          <Button color="primary" size="large" className="w-48" onClick={continuePractice}>
            接着练
          </Button>
          <button
            type="button"
            className="text-xs text-slate-400 underline"
            onClick={() => void restartFromResume()}
          >
            重新开始一次(放弃上次)
          </button>
        </div>
      </div>
    );
  }

  // ── 暂停屏(切后台 / 锁屏 / 麦克风被抢);字幕留在下面不清空 ──────
  if (phase === "paused") {
    const turnsSaid = messages.filter((m) => m.role === "user").length;
    return (
      <div className="flex flex-col h-full">
        <NavBar onBack={handleBack} className="border-b border-border bg-card shrink-0">
          {scene.title ?? scene.name}
        </NavBar>
        <div className="flex-1 flex flex-col items-center justify-center gap-5 px-8 bg-muted">
          <PartnerAvatar phase="paused" src={partnerImg} />
          <div className="text-center space-y-1.5">
            <p className="text-base font-semibold text-slate-900">对练已暂停</p>
            <p className="text-xs text-slate-500 leading-relaxed">
              {pauseReason === "mic-lost" ? (
                <>
                  麦克风被其他应用占用了(在通话或录音?)。
                  <br />
                  结束之后点下面继续,刚才说的都还在。
                </>
              ) : (
                <>
                  刚才离开了一下,已经帮你停在
                  {turnsSaid > 0 ? `第 ${turnsSaid} 轮` : "开头"}。
                  <br />
                  麦克风已经关了,点下面接着练。
                </>
              )}
            </p>
          </div>
          <Button
            color="primary"
            size="large"
            className="w-48"
            onClick={() => void resumePractice()}
          >
            继续对练
          </Button>
          <button type="button" className="text-xs text-slate-400 underline" onClick={handleBack}>
            先退出,下次再练
          </button>
        </div>
      </div>
    );
  }

  // ── 拨号屏(V2 L1):权限已拿到,正在等第一句。与「正在接通」分开,给真实的打电话感 ──
  if (phase === "starting" && dialing) {
    const slow = dialWaitMs >= DIAL_VERY_SLOW_MS;
    const abitSlow = dialWaitMs >= DIAL_SLOW_MS;
    return (
      <div className="flex flex-col h-full">
        <NavBar onBack={handleBack} className="border-b border-border bg-card shrink-0">
          {scene.title ?? scene.name}
        </NavBar>
        <div className="flex-1 flex flex-col items-center justify-center gap-6 px-8 bg-muted">
          <div className="relative flex items-center justify-center">
            {/* 拨号:两圈向外扩散的波纹(区别于思考态的托腮点点点) */}
            <span className="absolute h-44 w-44 rounded-full bg-sky-400/20 animate-ping" />
            <span
              className="absolute h-52 w-52 rounded-full bg-sky-400/10 animate-ping"
              style={{ animationDelay: "600ms" }}
            />
            <img
              src={partnerImg}
              alt="AI 客户"
              className="relative h-36 w-36 rounded-full object-cover object-top border-4 border-white shadow-lg"
            />
          </div>
          <div className="text-center space-y-1.5">
            <p className="text-base font-semibold text-slate-900">正在拨通对方…</p>
            <p className="text-xs text-slate-500 leading-relaxed">
              {slow
                ? "这次接得有点慢,再等等或者退出重试"
                : abitSlow
                  ? "对方那边好像有点忙…"
                  : "接通后像打电话一样开口就行"}
            </p>
          </div>
          <button
            type="button"
            className="text-xs text-slate-400 underline"
            onClick={toggleRingMuted}
          >
            {ringMuted ? "🔇 回铃音已关(点这里开)" : "🔔 关掉回铃音"}
          </button>
          {slow && (
            <Button size="small" fill="outline" onClick={cancelStart}>
              退出重试
            </Button>
          )}
        </div>
      </div>
    );
  }

  // ── 起始屏(一次点击拿权限;拒权指引也在这里) ────────────────
  if (phase === "start" || phase === "starting") {
    return (
      <div className="flex flex-col h-full">
        <NavBar onBack={handleBack} className="border-b border-border bg-card shrink-0">
          {scene.title ?? scene.name}
        </NavBar>
        <div className="flex-1 flex flex-col items-center justify-center gap-6 px-8 bg-muted">
          <PartnerAvatar phase={phase} src={partnerImg} />
          {/* 任务简报:第一次练这个场景时直接摆在起始屏(不多一次点击);练过的人可点开 */}
          {showBriefing && briefing.hasContent && (
            <div className="w-full rounded-xl bg-white border border-slate-200 px-4 py-3 shadow-sm space-y-2.5">
              {briefing.who && (
                <div>
                  <p className="text-2xs text-slate-400 mb-0.5">你要面对的客户</p>
                  <p className="text-sm text-slate-900 leading-relaxed">{briefing.who}</p>
                </div>
              )}
              {briefing.goals.length > 0 && (
                <div>
                  <p className="text-2xs text-slate-400 mb-1">这次练什么</p>
                  <div className="flex flex-wrap gap-1.5">
                    {briefing.goals.map((g) => (
                      <span
                        key={g}
                        className="text-2xs px-2 py-0.5 rounded bg-slate-100 text-slate-700"
                      >
                        {g}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {briefing.level && <p className="text-2xs text-slate-400">难度 · {briefing.level}</p>}
            </div>
          )}
          <div className="text-center space-y-1.5">
            <p className="text-base font-semibold text-slate-900">语音对练</p>
            <p className="text-xs text-slate-500 leading-relaxed">
              接通后 AI 扮演客户和你通话,像打电话一样开口应对即可。
              <br />
              说完停 2 秒,对方就会回应;全程无需按任何按钮。
            </p>
            {/* 练过的人:简报收起来,想看再点 */}
            {!showBriefing && briefing.hasContent && (
              <button
                type="button"
                className="text-2xs text-slate-400 underline"
                onClick={() => setShowBriefing(true)}
              >
                看看这次练什么
              </button>
            )}
            {/* 常亮兜底:不支持 Wake Lock 的老 webview 只能靠用户自己调锁屏时间 */}
            {!wakeLock.supported && (
              <p className="text-2xs text-slate-400 leading-relaxed">
                当前浏览器不支持自动常亮,建议先把手机的自动锁屏时间调长一点
              </p>
            )}
          </div>
          {micDenied && (
            <div className="rounded-lg bg-amber-50 px-4 py-3 text-xs text-amber-700 leading-relaxed whitespace-pre-wrap">
              {micReason ??
                "没拿到麦克风权限,语音对练无法开始。请开启企业微信的麦克风权限后回到本页重试。"}
            </div>
          )}
          {!support.ok && !micDenied && (
            <p className="text-2xs text-slate-400">
              检测到当前环境可能不支持 H5 录音,点开始查看详情
            </p>
          )}
          {startStuck && (
            <div className="space-y-2 text-center">
              <div className="rounded-lg bg-amber-50 px-4 py-3 text-xs text-amber-700 leading-relaxed">
                一直卡在接通中、也没见到授权弹框?
                <br />
                通常是当前企业微信版本(如鸿蒙版)还没开放 H5 录音。
                <br />
                建议换 iOS / 安卓手机的企微重试,或升级企业微信到最新版。
              </div>
              <Button size="small" fill="outline" onClick={cancelStart}>
                取消接通
              </Button>
            </div>
          )}
          <Button
            color="primary"
            size="large"
            className="w-48"
            loading={phase === "starting"}
            onClick={handleStart}
          >
            {phase === "starting"
              ? "接通中…"
              : showBriefing && briefing.hasContent
                ? "准备好了,接通"
                : "开始对练"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <NavBar onBack={handleBack} className="border-b border-border bg-card shrink-0">
        {scene.title ?? scene.name}
      </NavBar>

      {/* 小人 + 状态 */}
      {phase !== "finished" && (
        <div className="shrink-0 flex flex-col items-center gap-2 pt-5 pb-3 bg-muted">
          <PartnerAvatar phase={phase} src={partnerImg} />
          <p className={`text-xs ${phase === "listening" ? "text-emerald-600" : "text-slate-500"}`}>
            {hint ?? stateLabel[phase]}
          </p>
          {/* 交卷出口:说满 2 轮才亮(防误触空卷) */}
          {turnsSaid >= MIN_TURNS_TO_FINISH && voiceApi.finish && (
            <button
              type="button"
              className="text-2xs text-slate-500 border border-slate-300 rounded-full px-3 py-1 bg-white"
              onClick={() => void finishNow()}
            >
              结束并评分
            </button>
          )}
        </div>
      )}

      {/* 字幕区(文字仍是唯一真相源) */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-muted">
        {messages.map((msg, i) => (
          <div
            key={msg.id}
            className={`flex fx-enter ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            style={{ animationDelay: `${Math.min(i, 7) * 30}ms` }}
          >
            {msg.role === "assistant" && (
              <img
                src={partnerImg}
                alt=""
                className="w-8 h-8 rounded-full object-cover object-top shrink-0 mr-2 mt-0.5"
              />
            )}
            <div
              className={`max-w-[75%] px-3 py-2 rounded-xl text-sm leading-relaxed whitespace-pre-wrap ${
                msg.role === "user"
                  ? "bg-info text-info-foreground rounded-br-sm"
                  : "bg-white text-slate-900 border border-slate-200 rounded-bl-sm shadow-sm"
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}

        {/* 结束评分卡:三个陪练页共用同一个组件(档位 / 特效 / 文案都在里面) */}
        {phase === "finished" && (
          <ScoreResultCard
            status={evalStatus}
            score={score}
            feedback={feedback}
            lowConfidence={isLowConfidence}
            highlights={highlights}
            improvements={improvements}
            onBack={handleBack}
            backLabel={taskId ? t("wecomChat.backHome") : t("wecomChat.backScenes")}
            // 自主练习随时可以重来;任务制的补考另有闸(批次B),这里不给入口
            onRetry={taskId ? undefined : restartPractice}
          />
        )}

        <div ref={bottomRef} />
      </div>
    </div>
  );
}
